import React, { createContext, useContext, useState, useEffect } from 'react';
import { User } from '../../shared/types.ts';
import { useAuth } from './AuthContext.tsx';

interface UserProfileModalContextType {
  openUserProfile: (userOrId: User | string) => void;
  closeUserProfile: () => void;
  selectedUser: User | null;
  isOpen: boolean;
  navigateToChatWithUser: (user: User) => void;
  setChatNavigationCallback: (cb: (channelId: string) => void) => void;
}

const UserProfileModalContext = createContext<UserProfileModalContextType>({
  openUserProfile: () => {},
  closeUserProfile: () => {},
  selectedUser: null,
  isOpen: false,
  navigateToChatWithUser: () => {},
  setChatNavigationCallback: () => {},
});

export const UserProfileModalProvider: React.FC<{
  children: React.ReactNode;
  onNavigateToChat?: (channelId?: string) => void;
}> = ({ children, onNavigateToChat }) => {
  const { token, user: currentUser } = useAuth();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [teamMembers, setTeamMembers] = useState<User[]>([]);
  const [chatNavCallback, setChatNavCallback] = useState<((channelId: string) => void) | null>(null);

  // Cache team members
  useEffect(() => {
    if (!token) return;
    fetch('/api/team/members', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => setTeamMembers(d.members || []))
      .catch(() => {});
  }, [token]);

  const openUserProfile = (userOrId: User | string) => {
    if (typeof userOrId === 'string') {
      const found = teamMembers.find((m) => m.id === userOrId);
      if (found) {
        setSelectedUser(found);
      } else {
        // Fallback fetch
        if (token) {
          fetch(`/api/team/members`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.json())
            .then((d) => {
              const f = (d.members || []).find((m: User) => m.id === userOrId);
              if (f) setSelectedUser(f);
            })
            .catch(() => {});
        }
      }
    } else {
      setSelectedUser(userOrId);
    }
    setIsOpen(true);
  };

  const closeUserProfile = () => {
    setIsOpen(false);
    setSelectedUser(null);
  };

  const navigateToChatWithUser = async (targetUser: User) => {
    if (!token) return;
    try {
      // Find or create DM channel
      const res = await fetch('/api/chat/channels', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: 'direct',
          targetUserId: targetUser.id,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        closeUserProfile();
        if (data.channelId && chatNavCallback) {
          chatNavCallback(data.channelId);
        } else if (onNavigateToChat) {
          onNavigateToChat(data.channelId);
        }
      }
    } catch (e) {
      console.error('Failed to open DM channel:', e);
    }
  };

  const setChatNavigationCallback = React.useCallback((cb: (channelId: string) => void) => {
    setChatNavCallback(() => cb);
  }, []);

  const value = React.useMemo(
    () => ({
      openUserProfile,
      closeUserProfile,
      selectedUser,
      isOpen,
      navigateToChatWithUser,
      setChatNavigationCallback,
    }),
    [openUserProfile, closeUserProfile, selectedUser, isOpen, navigateToChatWithUser, setChatNavigationCallback]
  );

  return (
    <UserProfileModalContext.Provider value={value}>
      {children}
    </UserProfileModalContext.Provider>
  );
};

export const useUserProfileModal = () => useContext(UserProfileModalContext);
